import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskHeading } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Overline = ({ children, className = "" }) => (
  <span className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-brand-green ${className}`}>
    <span className="h-px w-8 bg-brand-green/60" />
    {children}
  </span>
);

export const Btn = ({ to, href, children, variant = "primary", className = "", testid, onClick, type }) => {
  const base = "group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-[transform,background-color,color,border-color] duration-300 hover:-translate-y-0.5";
  const styles = {
    primary: "bg-brand-green text-white hover:bg-brand-dark",
    dark: "bg-brand-dark text-brand-main hover:bg-brand-green",
    outline: "border border-brand-dark/20 text-brand-dark hover:border-brand-green hover:text-brand-green",
    light: "bg-white text-brand-dark hover:bg-brand-green hover:text-white",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  const inner = (<>{children}<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>);
  if (to) return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
  if (href) return <a href={href} data-testid={testid} className={cls}>{inner}</a>;
  return <button type={type || "button"} onClick={onClick} data-testid={testid} className={cls}>{inner}</button>;
};

export function StatCounter({ value, suffix = "", label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf; const start = performance.now(); const dur = 1600;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return (
    <div ref={ref}>
      <div className="font-serif text-5xl md:text-6xl leading-none text-brand-leaf">{n}{suffix}</div>
      <p className="mt-3 text-sm text-brand-main/70 max-w-[15rem]">{label}</p>
    </div>
  );
}

export function PageHero({ overline, title, lines, intro, image, imageAlt }) {
  return (
    <header className="relative overflow-hidden bg-brand-dark pt-36 pb-20 md:pt-44 md:pb-28">
      <div className="absolute inset-0 opacity-25">
        <img src={image} alt={imageAlt || ""} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-brand-dark/70" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6">
        <Reveal><Overline className="text-brand-leaf">{overline}</Overline></Reveal>
        <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-brand-main">
          <MaskHeading lines={lines || [title]} />
        </h1>
        {intro && <Reveal delay={0.3}><p className="mt-8 max-w-2xl text-lg text-brand-main/70">{intro}</p></Reveal>}
      </div>
    </header>
  );
}

export const Section = ({ children, className = "", dark = false, id }) => (
  <section id={id} className={`${dark ? "bg-brand-dark text-brand-main" : ""} py-24 md:py-32 ${className}`}>
    <div className="mx-auto max-w-7xl px-6">{children}</div>
  </section>
);

const FIELD = "w-full border-0 border-b border-brand-dark/20 bg-transparent px-0 py-3 text-brand-dark placeholder:text-brand-slate/50 focus:border-brand-green focus:outline-none focus:ring-0 transition-colors";

export function EnquiryForm({ kind, fields, submitLabel = "Send enquiry", testid, note }) {
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const known = ["name", "email", "phone", "organisation", "subject", "message"];
      const meta = {};
      Object.keys(data).forEach((k) => { if (!known.includes(k)) meta[k] = data[k]; });
      const payload = { kind, meta };
      known.forEach((k) => { if (data[k]) payload[k] = data[k]; });
      const res = await fetch(`${API}/enquiries`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Thank you — we've received your enquiry and will be in touch shortly.");
      setData({});
      e.target.reset();
    } catch { toast.error("Something went wrong. Please try again or call us."); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid={testid} className="space-y-6">
      {fields.map((f) => (
        <div key={f.name}>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-slate">{f.label}</label>
          {f.type === "textarea" ? (
            <textarea required={f.required} rows={4} data-testid={`${testid}-${f.name}`} className={FIELD} placeholder={f.placeholder} onChange={set(f.name)} />
          ) : f.type === "select" ? (
            <select required={f.required} data-testid={`${testid}-${f.name}`} className={FIELD} onChange={set(f.name)} defaultValue="">
              <option value="" disabled>{f.placeholder || "Select…"}</option>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input type={f.type || "text"} required={f.required} data-testid={`${testid}-${f.name}`} className={FIELD} placeholder={f.placeholder} onChange={set(f.name)} />
          )}
        </div>
      ))}
      {note && <p className="text-xs text-brand-slate/70">{note}</p>}
      <button type="submit" disabled={busy} data-testid={`${testid}-submit`} className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-brand-dark disabled:opacity-60">
        {busy ? "Sending…" : submitLabel}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
}

export const IconTile = ({ icon: Icon, title, desc, index }) => (
  <motion.div
    variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}
    transition={{ duration: 0.6 }}
    className="group relative rounded-2xl border border-brand-dark/10 bg-brand-surface p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-green/40"
  >
    {index && <span className="font-mono-a text-xs text-brand-slate/50">{index}</span>}
    <Icon className="mt-4 h-8 w-8 text-brand-green" strokeWidth={1.5} />
    <h3 className="mt-5 font-serif text-xl text-brand-dark">{title}</h3>
    <p className="mt-3 text-sm leading-relaxed text-brand-slate">{desc}</p>
  </motion.div>
);
